import { DeviceWithInstalledPower, MotorDataType, TargetRatedPowerGroupType } from '@e2m-customer-portal/domains';
import { MarketLocation } from '@e2m-customer-portal/hooks';
import { convertMeasure, Nullable } from '@e2m-customer-portal/shared';
import { Button, IconButton } from '@mui/material';
import { BinIcon, EditIcon, SettingsIcon } from 'assets/icons';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface OptimizationTargetRatedPowerGroupProps {
  index: number;
  data: TargetRatedPowerGroupType;
  items: Nullable<Map<string, MotorDataType>>;
  marketLocations: Array<MarketLocation>;
}

export function OptimizationTargetRatedPowerGroup({
  index,
  data,
  items,
  marketLocations,
}: Readonly<OptimizationTargetRatedPowerGroupProps>): ReactElement<OptimizationTargetRatedPowerGroupProps> {
  const [t] = useTranslation();

  const allDevices = marketLocations
    .flatMap((marketLocation) => marketLocation.machines)
    .flatMap((machine) =>
      machine.devices.map(
        (device) =>
          ({
            ...device,
            installedPower: machine.installedPower,
          }) satisfies DeviceWithInstalledPower,
      ),
    );
  const itemMetaData =
    items &&
    new Map(
      [...items].map(([identifier, item]) => [
        identifier,
        allDevices.find((device) => device.internalId === String(item.device_number)),
      ]),
    );

  return (
    <>
      <div
        className="hidden md:block absolute top-0 left-32 h-6 border-solid border border-b-0 rounded-t border-blue-grey-100"
        style={{
          width: `${(index + 1) * 17}rem`,
        }}
      />
      <div className="flex flex-col gap-y-3 w-64 pt-2 pb-4 px-3 box-border border-solid border border-blue-grey-100 rounded-lg">
        <div className="flex justify-between items-center">
          <h3 className="m-0 font-bold text-sm">{data.name}</h3>
          <IconButton disabled className="-m-1 p-1 disabled:opacity-25 disabled:grayscale">
            <EditIcon className="text-green-200" />
          </IconButton>
        </div>
        {data.target_rated_power_eeg && (
          <div className="text-xs">
            {`${t('OPTIMIZATION_TARGET_RATED_POWER')}: `}
            <strong>{t('OPTIMIZATION_VALUE_KILOWATT', { value: data.target_rated_power_eeg * 1000 })}</strong>
          </div>
        )}
        {itemMetaData && (
          <div className="flex flex-col">
            {[...itemMetaData].map(([identifier, itemData]) => (
              <div
                key={identifier}
                className="flex items-center gap-x-2.5 py-2 border-solid border-0 border-t border-blue-grey-100"
              >
                <SettingsIcon />
                <div className="flex flex-col items-start grow">
                  <span className="text-[0.625rem]">{itemData?.name}</span>
                  <strong className="text-xs">
                    {itemData?.externalName || items.get(identifier)?.alias}
                    {itemData &&
                      ` (${t('OPTIMIZATION_VALUE_KILOWATT', { value: convertMeasure(itemData.installedPower, 'kW').value })})`}
                  </strong>
                </div>
                <IconButton disabled className="-m-1 p-1 disabled:opacity-25 disabled:grayscale">
                  <EditIcon className="text-green-200" />
                </IconButton>
                <IconButton disabled className="-m-1 p-1 disabled:opacity-25 disabled:grayscale">
                  <BinIcon className="text-error-500" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <Button disabled variant="outlined">
          {t('OPTIMIZATION_TARGET_RATED_POWER_GROUP_ADD_MOTOR')}
        </Button>
      </div>
    </>
  );
}
